
/**
 * VirtualizedTable is a highly customizable and performant table component that supports virtualized rendering of large datasets,
 * dynamic column widths, row heights, sorting, filtering, row selection, inline cell editing, and global actions.
 * 
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.page - Page-specific configuration, including columns and cardHeight.
 * @param {Object} props.tabsOptions - Tab-related options, including activeTab, filterValues, and setFilterValues.
 * @param {Object} props.tableOptions - Table configuration options such as includeSelect, manualColumnWidthsIndex, and manualColumnWidths.
 * @param {Array<Object>} props.processedData - The array of data objects to be displayed in the table (already filtered/sorted).
 * @param {Object} props.sortingOptions - Sorting state and handlers, including sortField, setSortField, sortOrder, and setSortOrder.
 * @param {Object} props.tableActions - Table action handlers and options, including handleSaveInfo, globalActionOptions, globalActionBtnLbl, globalActionExtraContent, and handleGlobalActionSubmit.
 * 
 * @returns {JSX.Element} The rendered virtualized table component.
 * 
 * @example
 * <VirtualizedTable
 *   page={{
 *     pg: 'users',
 *     cardHeight: 600,
 *     columns: [
 *       { dataField: 'name', text: 'Name', editable: true },
 *       { dataField: 'email', text: 'Email', editable: false }
 *     ]
 *   }}
 *   tabsOptions={{
 *     activeTab: 0,
 *     filterValues: {},
 *     setFilterValues: () => {}
 *   }}
 *   tableOptions={{
 *     includeSelect: true,
 *     manualColumnWidthsIndex: [0],
 *     manualColumnWidths: [200]
 *   }}
 *   processedData={[
 *     { _id: 1, name: 'John Doe', email: 'john@example.com' },
 *     { _id: 2, name: 'Jane Smith', email: 'jane@example.com' }
 *   ]}
 *   sortingOptions={{
 *     sortField: 'name',
 *     setSortField: () => {},
 *     sortOrder: 'asc',
 *     setSortOrder: () => {}
 *   }}
 *   tableActions={{
 *     handleSaveInfo: () => {},
 *     globalActionOptions: [{ value: 'delete', text: 'Delete' }],
 *     globalActionBtnLbl: 'Apply',
 *     globalActionExtraContent: null,
 *     handleGlobalActionSubmit: () => {}
 *   }}
 * />
 */
import { useState, useEffect, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';
import { Row } from 'reactstrap';
import { AvForm, AvField } from 'availity-reactstrap-validation';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useSmartColumnWidths } from './dynamicColumnWidths';
import { MeasureRows } from './dynamicRowHeights';
import MultiSelectDropdown from '../MultiSelect';
/**
 * Renders the content of a table row, including editable cells, formatted values, and optional selection checkbox.
 *
 * This function generates an array of React elements representing the cells of a table row. It supports:
 * - Editable cells with different input types (text, date, select) based on column configuration.
 * - Custom cell formatting via column.formatter.
 * - Row selection via a checkbox (if enabled).
 * - Special rendering for footer rows.
 *
 * @param {Object} row - The data object representing the current row.
 * @param {Array} columns - Array of column definitions, each containing dataField, editable, formatter, editor, and classes.
 * @param {number} index - The index of the current row.
 * @param {Array<number>} columnWidths - Array of widths for each column.
 * @param {Object} editingCell - The current editing cell, with shape { rowIndex: number, columnKey: string }.
 * @param {Function} setEditingCell - Function to set the currently editing cell.
 * @param {Function} handleCellSave - Callback to save the edited cell value.
 * @param {boolean} includeSelect - Whether to include a selection checkbox cell.
 * @param {Array<Object>} selectedRows - Array of currently selected row objects.
 * @param {Function} handleSelectRow - Callback to handle row selection changes.
 * @returns {Array<React.ReactNode>} Array of React elements representing the row's cells.
 */
const renderRowContent = (row, columns, index, columnWidths, editingCell, setEditingCell, handleCellSave, includeSelect, selectedRows, handleSelectRow) => {
  const isFooter = row._isFooter;
  const isSelected = selectedRows.some(selected => selected._id === row._id);
  // Checkbox cell
  const checkboxCell = (
    <div
      key="checkbox"
      className="td-div"
      style={{ width: 30, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      onClick={() => handleSelectRow(row, !isSelected)} // Toggle selection on div click
    >
      {!isFooter && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => handleSelectRow(row, e.target.checked)}
          onClick={(e) => e.stopPropagation()} // Prevent double toggling when checkbox itself is clicked
        />
      )}
    </div>
  );
  const dataCells = columns.map((column, i) => {
    const cellKey = column.dataField;
    const cellValue = cellKey.split('.').reduce((obj, key) => obj?.[key], row);
    const isEditing = editingCell.rowIndex === index && editingCell.columnKey === cellKey;
    const isEditable = column.editable === true;
    // Add footer
    if (isFooter) {
      return (
        <div key={i} className='td-div' style={{ width: columnWidths[i], minWidth: columnWidths[i], maxWidth: columnWidths[i] }}>
          {row[cellKey] || null}
        </div>
      );
    }
    // Value to dispaly
    const displayValue = typeof column.formatter === 'function' ? column.formatter(cellValue, row, index) : cellValue;
    return (
      <div
        key={i}
        className={`td-div ${column.classes}`} 
        style={{ width: columnWidths[i], minWidth: columnWidths[i], maxWidth: columnWidths[i], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        onClick={() => {
          if (!isEditable) return;
          if (!(editingCell.rowIndex === index && editingCell.columnKey === cellKey)) {
            setEditingCell({ rowIndex: index, columnKey: cellKey });
          }
        }}
      >
        {isEditing && isEditable ? (() => {
          const type = column.editor?.type;
          if (type === 'date') {
            const rawDate = (() => {
              if (!cellValue) return '';
              if (/^\d{4}-\d{2}-\d{2}T/.test(cellValue)) {
                const d = new Date(cellValue);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              }
              if (/^\d{2}\/\d{2}\/\d{4}/.test(cellValue)) {
                const parts = cellValue.split(',')[0].split('/');
                return `${parts[2]}-${parts[1]}-${parts[0]}`; // dd/mm/yyyy -> yyyy-mm-dd
              }
              return '';
            })();
            return (
              <input
                type='date'
                autoFocus
                defaultValue={rawDate}
                onClick={(e) => e.stopPropagation()}
                onBlur={(e) => {
                  const formattedDate = e.target.value
                    ? new Date(e.target.value).toLocaleDateString('en-GB') + ', 00:00:00'
                    : '';
                  handleCellSave(cellValue, formattedDate, row, column, index);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.target.blur();
                }}
                className='filter-input'
              />
            );
          }
          if (type === 'select' && Array.isArray(column.editor.options)) {
            return (
              <select
                autoFocus
                // defaultValue={cellValue}
                value={cellValue}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  handleCellSave(cellValue, e.target.value, row, column, index);
                }}
                // onBlur={(e) => handleCellSave(cellValue, e.target.value, row, column, index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.target.blur();
                }}
                className='filter-input'
              >
                {column.editor.options.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            );
          }
          // Default to text input
          return (
            <input
              type="text"
              autoFocus
              defaultValue={cellValue}
              onBlur={(e) => handleCellSave(cellValue, e.target.value, row, column, index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.target.blur();
              }}
              className='filter-input'
            />
          );
        })() : displayValue}
      </div>
    );
  });

  return [includeSelect && checkboxCell, ...dataCells];

  // return columns.map((column, i) => {
  //   const cellKey = column.dataField;
  //   const cellValue = cellKey.split('.').reduce((obj, key) => obj?.[key], row);
  //   const isEditing = editingCell.rowIndex === index && editingCell.columnKey === cellKey;
  //   const isEditable = column.editable === true;

  //   if (isFooter) {
  //     return (
  //       <div key={i} className='td-div text-center p-2' style={{ width: columnWidths[i], paddingTop: '10px', paddingBottom: '10px',  }}>
  //         {row[cellKey] || null}
  //       </div>
  //     );
  //   }

  //   return (
  //     <div key={i} className='td-div' style={{ width: columnWidths[i] }} onClick={() => isEditable && setEditingCell({ rowIndex: index, columnKey: cellKey })}>
  //       {isEditing ? (
  //         <input
  //           autoFocus
  //           defaultValue={cellValue}
  //           onBlur={(e) => handleCellSave(cellValue, e.target.value, row, column, index) }
  //           onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
  //           className='filter-input'
  //         />
  //       ) : typeof column.formatter === 'function' ? ( column.formatter(row[column.dataField], row, index) )
  //       : ( column.dataField.split('.').reduce((obj, key) => obj?.[key], row) )
  //       }
  //     </div>
  //   )
  // })
};

export const VirtualizedTable = ({ 
  page,
  tabsOptions,
  tableOptions,
  processedData,
  sortingOptions,
  tableActions
}) => {
  const { pg, cardHeight, columns, rowClasses } = page;
  const {sortField, setSortField, sortOrder, setSortOrder } = sortingOptions || {};
  const {activeTab, filterValues, setFilterValues } = tabsOptions || {};
  const {includeSelect, manualColumnWidthsIndex, manualColumnWidths } = tableOptions || {};
  const { handleSaveInfo, setSelectedRowsMainComp, globalActionOptions, globalActionOptionsMulti = [], multiselect = false, selectedValues=[], setSelectedValues, globalActionBtnLbl, globalActionExtraContent, handleGlobalActionSubmit } = tableActions || {};
  const defaultRowHeight = 50;
  const cardHeightDecrease = pg === 'fees' ? cardHeight * 0.3 : cardHeight * 0.23;
  const tableRef = useRef(null);
  const tableRefContent = useRef(null);
  const headerRefs = useRef([]);
  const listRef = useRef();
  const selectAllRef = useRef(null);
  const formRef = useRef();
  const [editingCell, setEditingCell] = useState({ rowIndex: null, columnKey: null });
  const [selectedRows, setSelectedRows] = useState([]);
  /**
   * Handles the selection or deselection of a table row.
   *
   * Updates the selected rows state by either adding the specified row
   * when selected, or removing it by its unique `_id` when deselected.
   *
   * @param {Object} row - The row object to select or deselect.
   * @param {boolean} isSelected - Indicates whether the row is being selected (true) or deselected (false).
   */
  const handleSelectRow = (row, isSelected) => {
    setSelectedRows(prev =>
      isSelected
        ? [...prev, row] // add full row
        : prev.filter(r => r._id !== row._id) // remove by id
    );
    if (typeof setSelectedRowsMainComp === 'function') {
      setSelectedRowsMainComp('single', row, isSelected);
    }
  };
  /**
   * Handles the selection or deselection of all rows in the table.
   *
   * @param {boolean} isSelectAll - Indicates whether to select all rows (true) or deselect all rows (false).
   * @param {Array<Object>} allRows - The array of all row objects available in the table.
   */
  const handleSelectAll = (isSelectAll, allRows) => {
    setSelectedRows(isSelectAll ? allRows.map(row => row) : []);
    if (typeof setSelectedRowsMainComp === 'function') {

      setSelectedRowsMainComp('all', allRows, isSelectAll);
    }
  };
  const allRowIds = processedData.filter(row => !row._isFooter).map(row => row._id);
  const isAllSelected = allRowIds.length > 0 && allRowIds.every(id => selectedRows.some(row => row._id === id));
  /**
   * Handles sorting logic for a table by updating the sort field and order.
   * If the selected field is already the current sort field, toggles the sort order between ascending and descending.
   * Otherwise, sets the new sort field and resets the sort order to ascending.
   *
   * @param {string} field - The name of the field to sort by.
   */
  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };
  /**
   * Updates the filter values for the currently active tab based on the provided field and value.
   * 
   * - If the value is an empty object or an empty string, the filter for the specified field is removed.
   * - If the value is an object (e.g., for date filters), it merges the new value with any existing filter for that field.
   * - If the value is a string (e.g., for text filters), it sets the filter for the specified field to that value.
   * - If all filters for the active tab are removed, the tab's filter entry is also removed.
   *
   * @param {string} field - The name of the field to filter.
   * @param {string|Object} value - The filter value, which can be a string (for text filters) or an object (for complex filters like date).
   */
  const handleFilter = (field, value) => {
    setFilterValues(prev => {
      const currentTabFilters = prev[activeTab] || {};
      // CLEAR: remove filter if value is empty object or empty string
      if ((typeof value === 'object' && Object.keys(value).length === 0) || value === '') {
        const updatedTabFilters = { ...currentTabFilters };
        delete updatedTabFilters[field];
        // if tab has no filters left, remove the tab key
        const newFilters = { ...prev };
        if (Object.keys(updatedTabFilters).length === 0) {
          delete newFilters[activeTab];
        } else {
          newFilters[activeTab] = updatedTabFilters;
        }
        return newFilters;
      }
      // DATE FILTER: if value is an object (comparator/date)
      if (typeof value === 'object') {
        const prevValue = currentTabFilters[field] || {};
        return {
          ...prev,
          [activeTab]: {
            ...currentTabFilters,
            [field]: { ...prevValue, ...value }
          }
        };
      }
      // TEXT FILTER: simple string
      return {
        ...prev,
        [activeTab]: {
          ...currentTabFilters,
          [field]: value
        }
      };
    });
    setScrollReady(false);
    setBodyScrollElement(null);
  };
  /**
   * Computes and returns the array of column widths for the table, using a custom hook.
   * 
   * This uses the `useSmartColumnWidths` hook to determine the width of each column based on:
   * - Manually specified column width indices and values (manualColumnWidthsIndex, manualColumnWidths)
   * - The column definitions and processed data
   * - The table DOM reference for dynamic measurement
   * 
   * @type {Array<number>}
   */
  const columnWidths = useSmartColumnWidths(manualColumnWidthsIndex, manualColumnWidths, columns, processedData, tableRef, tableRefContent);
  // const columnWidths = useSmartColumnWidths(columns, tableRef);
  // Set row height
  const [rowHeights, setRowHeights] = useState({});
  const allMeasured = Object.keys(rowHeights).length === processedData.length;
  const getItemSize = index => rowHeights[index] || defaultRowHeight;
  /**
   * Handles the saving of a cell's value in the virtualized table.
   * Normalizes both the old and new values for comparison, and if they differ,
   * triggers the save handler. Exits edit mode after processing.
   *
   * @param {*} oldVal - The previous value of the cell before editing.
   * @param {*} newVal - The new value entered by the user.
   * @param {Object} row - The data object representing the current row.
   * @param {Object} column - The column definition object for the cell.
   * @param {number} index - The index of the row in the data set.
   */
  const handleCellSave = (oldVal, newVal, row, column, index) => {
    const normalize = (val) => {
      if (Array.isArray(val) && val.length === 1) {
        return String(val[0]);
      }
      return String(val);
    };
    const normalizedOld = normalize(oldVal);
    const normalizedNew = normalize(newVal);

    if (normalizedOld !== normalizedNew) {
      handleSaveInfo(oldVal, newVal, row, column, index);
    }
    setEditingCell({ rowIndex: null, columnKey: null }); // exit edit mode
  };
  /**
   * Handles the global form submission event.
   * Submits the selected rows and form values to the global action handler,
   * then resets the selected rows and form fields to their default state.
   *
   * @param {Event} e - The form submission event.
   * @param {Object} values - The values from the submitted form.
   */
  const handleGlobalSubmit = (e, values) => {
    handleGlobalActionSubmit(selectedRows, values, selectedValues)
    // Reset form fields to default
    setSelectedRows([])
    formRef.current.reset();
  }
  // Sync scrolling
  const [scrollReady, setScrollReady] = useState(false);
  const headerScrollRef = useRef(null); // The <div> wrapping the <table>
  const bodyScrollRef = useRef(null);   // The outer scrollable div from react-window
  const [bodyScrollElement, setBodyScrollElement] = useState(null);
  useEffect(() => {
    if (!scrollReady || !bodyScrollElement || !headerScrollRef.current) return;
    const body = bodyScrollElement;
    const header = headerScrollRef.current;
    // if (!body || !header) return;
    const syncScroll = () => {
      header.scrollLeft = body.scrollLeft;
    };
    body.addEventListener('scroll', syncScroll);
    return () => {
      body.removeEventListener('scroll', syncScroll);
    };
  }, [scrollReady, bodyScrollElement]);

  const [scrollOffset, setScrollOffset] = useState(0);
  useEffect(() => {
    if (!scrollReady || !bodyScrollElement) return;

    const handleScroll = (e) => {
      setScrollOffset(e.target.scrollTop);
      headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    };

    bodyScrollElement.addEventListener('scroll', handleScroll);
    return () => {
      bodyScrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [scrollReady, bodyScrollElement]);
  /**
   * Renders a single row in the virtualized table.
   *
   * @function
   * @param {Object} props - The properties object.
   * @param {number} props.index - The index of the row to render.
   * @param {Object} props.style - The style object for positioning and sizing the row (provided by the virtualizer).
   * @returns {JSX.Element} The rendered table row component.
   */
  const TableRow = ({ index, style }) => {
    const row = processedData[index];
    const isLastRow = index === processedData.length - 1;
    const rowClass = rowClasses ? rowClasses(row, index) : '';
    return (
      <div style={{ ...style, borderBottom: isLastRow ? '2px solid #d4d4d5' : 'none' }}  className={`tr-div ${rowClass}`}>
        {renderRowContent(row, columns, index, columnWidths, editingCell, setEditingCell, handleCellSave, includeSelect, selectedRows, handleSelectRow)}
      </div>
    );
  };
  /**
   * useEffect to set the indeterminate state of the "Select All" checkbox.
   * 
   * This effect runs whenever the selectedRows or processedData changes.
   * It updates the indeterminate property of the selectAllRef checkbox to visually indicate
   * when some, but not all, rows are selected (i.e., a partial selection).
   */
  useEffect(() => {
    if (!selectAllRef.current) return;

    const allIds = processedData.filter(row => !row._isFooter).map(row => row._id);
    const selectedIds = selectedRows.map(row => row._id);

    const isIndeterminate =
      selectedIds.length > 0 &&
      selectedIds.length < allIds.length;

    selectAllRef.current.indeterminate = isIndeterminate;
  }, [selectedRows, processedData]);
  return (
    <Row id='table-section'>
      <div style={{ overflowX: 'hidden', padding: '0px' }} ref={headerScrollRef}>
        <div ref={tableRef}>
          <table id="tableScroll" style={{ tableLayout: "fixed", width: "100%" }} className="table table-bordered align-middle table-nowrap mb-0">
            <thead>
              <tr className="header-class is-sticky-hdr">
                {includeSelect && (
                  <th key="select-all" className="table-cell" style={{ width: 30, minWidth: 30, maxWidth: 30, textAlign: 'center', whiteSpace: 'break-spaces' }}>
                    <input ref={selectAllRef} type="checkbox" checked={isAllSelected} onChange={(e) => handleSelectAll(e.target.checked, processedData)}/>
                  </th>
                )}
                {columns.map((column, index) => (
                  <th 
                    key={column.dataField}
                    ref={(el) => (headerRefs.current[index] = el)}
                    className={`table-cell ${column.headerClasses} ${sortField === column.dataField ? 'sorted' : ''}`}
                    onClick={() => handleSort(column.dataField)}
                    style={{ width: columnWidths[index], minWidth: columnWidths[index], maxWidth: columnWidths[index], whiteSpace: 'break-spaces' }}
                  >
                    {column.text}
                    {sortField === column.dataField && (
                      <span className="sort-indicator">
                        {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                    {column.filter && (
                      column.editor?.type === 'select' ? (
                        <select className="filter-input" value={filterValues?.[activeTab]?.[column.dataField] || ''} onClick={(e) => e.stopPropagation()} onChange={(e) => handleFilter(column.dataField, e.target.value)}>
                          <option value=''>All</option>
                          {column.editor.options.map(({ value, label }) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      ) : column.editor?.type === 'date' ? (
                        <div className='d-flex'>
                          <select className="filter-input filter-input-comp" value={filterValues?.[activeTab]?.[column.dataField]?.comparator || ''} onClick={(e) => e.stopPropagation()} onChange={(e) => handleFilter(column.dataField, {...filterValues[column.dataField], comparator: e.target.value })}>
                            <option value=""></option>
                            <option value="=">=</option>
                            <option value=">">{'>'}</option>
                            <option value="<">{'<'}</option>
                            <option value=">=">{'>='}</option>
                            <option value="<=">{'<='}</option>
                          </select>
                          <input
                            type="date"
                            className="filter-input filter-input-date"
                            onClick={(e) => e.stopPropagation()}
                            value={filterValues?.[activeTab]?.[column.dataField]?.date || ''}
                            onChange={(e) => handleFilter(column.dataField, {...filterValues[column.dataField], date: e.target.value })}
                          />
                          {filterValues?.sendDate && Object.keys(filterValues.sendDate).length > 0 &&
                            <i 
                              className='text-danger mdi mdi-delete font-size-18' style={{ cursor: 'pointer', paddingTop: '4px' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFilter(column.dataField, {}); // clear comparator and date
                              }}
                            ></i>
                          }
                        </div>
                      ) : (
                        <input
                          type="text"
                          placeholder={`Filter ${column.text}`}
                          onChange={(e) => handleFilter(column.dataField, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          value={filterValues?.[activeTab]?.[column.dataField] || ''}
                          className="filter-input"
                        />
                      )
                    )}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      </div>
      {cardHeight && (
      <div
        className='table-start'
        id='table-start'
        style={{ height: `calc(${cardHeight}px - ${cardHeightDecrease}px)`, padding: '0px' }}
      >
        {!allMeasured && (
          <MeasureRows
            data={processedData}
            columns={columns}
            onMeasure={setRowHeights}
            renderRowContent={(row, columns, index) =>
              renderRowContent(row, columns, index, columnWidths, editingCell, setEditingCell, handleCellSave, includeSelect, selectedRows, handleSelectRow)
            }
            defaultRowHeight={defaultRowHeight}
          />
        )}

        {allMeasured && processedData.length === 1 && processedData[0].manager === "No Results Found" ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontStyle: 'italic',
              color: '#888',
            }}
          >
            No Results Found
          </div>
        ) : allMeasured && (
          <AutoSizer>
            {({ height, width }) => (
              <List
                key={processedData.length}
                ref={(ref) => {
                  if (ref && ref._outerRef !== bodyScrollRef.current) {
                    listRef.current = ref;
                    bodyScrollRef.current = ref._outerRef;
                    setBodyScrollElement(ref._outerRef);
                    setScrollReady(true);
                  }
                }}
                height={height}
                width={width}
                itemCount={processedData.length}
                itemSize={getItemSize}
                estimatedItemSize={defaultRowHeight}
                initialScrollOffset={scrollOffset}
                style={{ paddingBottom: '15px' }}
                className='react-bootstrap-table'
              >
                {TableRow}
              </List>
            )}
          </AutoSizer>
        )}
      </div>
      )}
      {globalActionOptions && (
        <div className={selectedRows.length === 0 ? 'action_row hidden' : 'action_row'}>
          <AvForm ref={formRef} onValidSubmit={handleGlobalSubmit}>
            {multiselect ?
                <MultiSelectDropdown
                  options={globalActionOptionsMulti}
                  // value={selectedValues}
                  onChange={(values) => setSelectedValues(values)}
                />
              :   
                <AvField type='select' name='action' className='form-select'>
                  <option value='SELECT ACTION'>-- Select Action --</option>
                  {Array.isArray(globalActionOptions)
                    ? globalActionOptions.map((action, index) => (
                        <option key={index} value={action.value} className={`${action.className || ''} fw-bold fs-5`}>
                          {action.text}
                        </option>
                      ))
                    : Object.entries(globalActionOptions).map(([group, options]) => (
                        <optgroup
                          key={group}
                          label={group.charAt(0).toUpperCase() + group.slice(1)}
                        >
                          {options.map((action, index) => (
                            <option key={`${group}-${index}`} value={action.value} className="fw-bold fs-5">
                              {action.text}
                            </option>
                          ))}
                        </optgroup>
                      ))
                  }
                </AvField>
            }
            {pg !== 'transfers' && globalActionExtraContent}
            <button className='btn btn-success'> {globalActionBtnLbl} </button>
            {pg === 'transfers' && globalActionExtraContent}
          </AvForm>
          <div className="sidebar-background"></div>
        </div>
      )}
    </Row>
  );
}